import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// City-specific accent variations (subtle)
const cityAccents = {
  atlanta: { glow: "rgba(15,59,46,0.12)" },
  austin: { glow: "rgba(15,59,46,0.14)" },
  chicago: { glow: "rgba(15,59,46,0.13)" },
  dallas: { glow: "rgba(15,59,46,0.12)" },
  houston: { glow: "rgba(15,59,46,0.15)" },
  "los-angeles": { glow: "rgba(15,59,46,0.11)" },
  miami: { glow: "rgba(15,59,46,0.10)" },
  "new-york": { glow: "rgba(15,59,46,0.13)" },
  "san-diego": { glow: "rgba(15,59,46,0.12)" },
};

// Icons for experience section
const IconBeats = () => (
  <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="18" stroke="#D4AF37" strokeWidth="2.5"/>
    <circle cx="24" cy="24" r="7" stroke="#C24E2C" strokeWidth="2.5"/>
    <circle cx="24" cy="24" r="2.5" fill="#D4AF37"/>
  </svg>
);

const IconTequila = () => (
  <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
    <path d="M16 10H32L29 38H19L16 10Z" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 10H34" stroke="#C24E2C" strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M21 38H27" stroke="#C24E2C" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const IconVenues = () => (
  <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
    <circle cx="12" cy="18" r="5" stroke="#C24E2C" strokeWidth="2.5"/>
    <circle cx="36" cy="18" r="5" stroke="#C24E2C" strokeWidth="2.5"/>
    <circle cx="24" cy="34" r="5" stroke="#D4AF37" strokeWidth="2.5"/>
    <path d="M16 20L20 32" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"/>
    <path d="M32 20L28 32" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const CityComingSoon = ({ citySlug, cityName, cityData }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Use cityData from API if available
  const displayName = cityData?.cityName || cityName || citySlug?.toUpperCase();
  const headline = cityData?.comingSoonHeadline || "ARE YOU READY?";
  const subline = cityData?.comingSoonSubline || "Tickets dropping soon.";
  const ctaText = cityData?.cityPageCtaText || "GET PRIORITY ACCESS";
  const emailEnabled = cityData?.emailCaptureEnabled !== false;
  
  const accents = cityAccents[citySlug?.split('-')[0]] || cityAccents.atlanta;
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email) return;
    
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${API_URL}/api/email-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone: phone || "",
          source: `city-${citySlug}`
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        if (data.detail === "Already signed up") {
          setError("You're already on the list!");
          setTimeout(() => setSubmitted(true), 1500);
        } else {
          setError("Something went wrong. Please try again.");
        }
      }
    } catch (err) {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const experienceItems = [
    { icon: IconBeats, text: "Non-stop beats." },
    { icon: IconTequila, text: "Premium tequila." },
    { icon: IconVenues, text: "One night. Every venue." },
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid={`city-page-${citySlug}`}>
      {/* Back Navigation */}
      <Link 
        to="/" 
        className="fixed top-24 left-6 z-40 flex items-center gap-2 text-white/50 hover:text-white transition-colors"
        data-testid="back-to-home"
      >
        <ChevronLeft className="w-5 h-5" />
        <span className="font-accent text-sm uppercase tracking-wider">Back</span>
      </Link>

      {/* ============================================
          1️⃣ HERO SECTION
          Clean, high impact, no form
          ============================================ */}
      <section 
        className="min-h-[85vh] flex flex-col items-center justify-center px-4 pt-20 pb-12 relative overflow-hidden"
        data-testid="city-hero"
      >
        {/* Deep green vignette */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse 80% 60% at 50% 40%, ${accents.glow} 0%, transparent 70%)`,
          }}
        />

        <motion.div 
          className="text-center max-w-4xl mx-auto relative z-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Brand */}
          <h1 className="mb-4">
            <span 
              className="block font-heading font-black text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight"
              style={{ color: "#F4F1E8" }}
            >
              CINCO DE CRAWL
            </span>
            <span 
              className="block font-heading font-black text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight mt-3"
              style={{ color: "#C24E2C" }}
            >
              {displayName}
            </span>
          </h1>

          {/* Gold divider */}
          <div className="w-16 h-0.5 mx-auto my-6" style={{ background: "#D4AF37" }} />

          {/* Date */}
          <p 
            className="font-heading font-bold text-xl md:text-2xl uppercase tracking-wide mb-3"
            style={{ color: "#D4AF37" }}
          >
            {cityData?.eventDate || "MAY 5, 2026"}
          </p>
          <p className="text-white/50 text-base md:text-lg font-light">
            {subline}
          </p>
        </motion.div>

        {/* Scroll indicator - subtle hint that more content exists */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center pt-2">
            <div className="w-1 h-2 bg-white/40 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* ============================================
          2️⃣ GET PRIORITY ACCESS - PRIMARY CONVERSION
          Single form, directly under hero
          ============================================ */}
      <section 
        className="py-16 md:py-24 px-4 relative"
        style={{ background: "#0A0A0A" }}
        data-testid="priority-access-section"
      >
        {/* Gold accent line at top */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-0.5" style={{ background: "#D4AF37" }} />

        <motion.div 
          className="max-w-md mx-auto text-center relative z-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 
            className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mb-3"
            style={{ color: "#F4F1E8" }}
          >
            GET <span className="text-terracotta">PRIORITY ACCESS</span>
          </h2>
          
          <p className="text-white/40 text-sm md:text-base mb-8 font-light">
            Be first to know when tickets go live.
          </p>

          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.form 
                onSubmit={handleSubmit} 
                className="flex flex-col gap-4" 
                key="form" 
                exit={{ opacity: 0 }}
              >
                <Input
                  type="text"
                  placeholder="Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-[#111111] border-white/10 text-white placeholder:text-white/30 py-5 px-5 text-base rounded-sm focus:border-[#D4AF37]"
                  required
                  data-testid="form-name"
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#111111] border-white/10 text-white placeholder:text-white/30 py-5 px-5 text-base rounded-sm focus:border-[#D4AF37]"
                  required
                  data-testid="form-email"
                />
                <Input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-[#111111] border-white/10 text-white placeholder:text-white/30 py-5 px-5 text-base rounded-sm focus:border-[#D4AF37]"
                  data-testid="form-phone"
                />
                {error && (
                  <p className="text-terracotta text-sm" data-testid="form-error">{error}</p>
                )}
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] py-5 font-accent font-bold text-sm tracking-wider rounded-sm mt-2 transition-all hover:-translate-y-0.5 disabled:opacity-50"
                  data-testid="form-submit"
                >
                  {loading ? "JOINING..." : "JOIN THE LIST"}
                </Button>
              </motion.form>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center gap-4 py-6"
                data-testid="form-success"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C24E2C] flex items-center justify-center shadow-lg shadow-[#D4AF37]/20">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <div className="text-center">
                  <p className="font-heading font-black text-2xl uppercase tracking-tight mb-1" style={{ color: "#F4F1E8" }}>
                    YOU'RE <span className="text-terracotta">IN!</span>
                  </p>
                  <p className="text-white/40 text-sm">We'll hit you up when tickets drop.</p>
                </div>
                <div className="w-12 h-0.5 mt-2" style={{ background: "#D4AF37" }} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* ============================================
          3️⃣ HYPE SECTION - ARE YOU READY?
          Reinforcement after form, pure energy
          ============================================ */}
      <section 
        className="py-20 md:py-32 px-4 relative overflow-hidden"
        style={{ background: "#050505" }}
        data-testid="hype-section"
      >
        {/* Subtle vignette */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse 70% 50% at 50% 50%, ${accents.glow} 0%, transparent 60%)`,
          }}
        />

        <motion.div 
          className="max-w-4xl mx-auto text-center relative z-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-[0.85]">
            <span className="block" style={{ color: "#C24E2C" }}>{cityName}</span>
            <span className="block mt-2" style={{ color: "#F4F1E8" }}>ARE YOU READY?</span>
          </h2>
          
          {/* Gold accent */}
          <div className="w-16 h-0.5 mx-auto mt-8" style={{ background: "#D4AF37" }} />
        </motion.div>
      </section>

      {/* ============================================
          4️⃣ EXPERIENCE FEATURES
          Clean grid, orange/gold icons
          ============================================ */}
      <section 
        className="py-16 md:py-24 px-4 relative"
        style={{ background: "#0A0A0A" }}
        data-testid="experience-section"
      >
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {experienceItems.map((item, i) => (
              <motion.div 
                key={item.text}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                <div className="flex justify-center mb-4">
                  <item.icon />
                </div>
                <p className="font-accent font-bold text-base md:text-lg uppercase tracking-wider" style={{ color: "#F4F1E8" }}>
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          FOOTER
          ============================================ */}
      <footer 
        className="py-10 px-4 border-t border-white/5"
        style={{ background: "#050505" }}
        data-testid="city-footer"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h4 className="font-heading font-bold text-lg mb-1" style={{ color: "#F4F1E8" }}>
            CINCO <span className="text-terracotta">DE</span> CRAWL
          </h4>
          <p className="text-white/30 text-xs mb-4">The Ultimate Bar Crawl Tour</p>
          <div className="w-8 h-0.5 mx-auto mb-4" style={{ background: "#1F4E3D" }} />
          <p className="text-white/20 text-xs">© 2026 Cinco De Crawl. Must be 21+.</p>
        </div>
      </footer>
    </div>
  );
};

export default CityComingSoon;
